import { css } from 'styled-components'

/**
 * The token page's own layout, in the board's language.
 *
 * The palette, the chrome and the mono figures come from the Pulse stylesheet
 * this is mounted beside. What is here is the arrangement: a header that leads
 * with one figure instead of twenty, a chart that takes the room it needs, and
 * the panels beside it.
 */
export const tradeStyles = css`
  .page {
    flex-grow: 1;
    min-height: 0;
    display: grid;
    gap: 8px;
    padding: 8px 16px;
    grid-template-columns: minmax(0, 1fr) 320px;
    grid-template-rows: auto minmax(0, 1fr);
    grid-template-areas:
      'head head'
      'main side';
  }

  /*
   * The ladder, on the stops the board already uses. Three columns while they
   * fit; the tape folds before the chart is squeezed; the rail goes above the
   * chart when there is only one column, because on a narrow screen the thing
   * somebody came to do is buy.
   */
  @container pulse (max-width: 1100px) {
    .page {
      grid-template-columns: minmax(0, 1fr);
      grid-template-areas:
        'head'
        'side'
        'main';
      overflow-y: auto;
      scrollbar-width: none;
    }

    .page::-webkit-scrollbar {
      display: none;
    }

    .side {
      overflow: visible;
    }

    /* Beside each other rather than stacked: the rail's blocks are narrow and
       a phone-shaped column of them is a very long scroll on a tablet. */
    .side {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      align-items: start;
    }
  }

  @container pulse (max-width: 639px) {
    .side {
      grid-template-columns: minmax(0, 1fr);
    }

    .chartrow {
      min-height: 240px;
    }

    /*
     * The footer is hidden at this width by the board's own stylesheet, and
     * the one thing in it that matters on a phone is buying. So the trade
     * panel stops scrolling away.
     */
    .side > .panel {
      position: sticky;
      bottom: 0;
      z-index: 2;
    }
  }

  /* ---------- the strip of other tokens ---------- */

  /*
   * The board's own bar, carrying a ticker. Both terminals run one in exactly
   * this slot, and we already had the slot — Pulse and Portfolio put their
   * title and controls in it, so the strip inherits its height, its gutter and
   * its narrow-width behaviour without a new piece of chrome.
   */
  .tickbar {
    gap: 10px;
  }

  .ticks {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 2px;
    overflow: hidden;
  }

  .tick {
    flex-shrink: 0;
    height: 22px;
    padding: 0 7px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 5px;
  }

  .tick:hover {
    background: var(--chip);
  }

  .tick .tk {
    font-size: 11.5px;
    font-weight: 500;
    color: var(--t2);
  }

  .tick .tc {
    font-size: 11px;
    font-weight: 500;
  }

  .tickbar .right {
    margin-left: auto;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tickbar .dim {
    font-size: 10.5px;
    color: var(--t3);
  }

  /* ---------- the header ---------- */

  /*
   * One band, and the figures in it wear nothing.
   *
   * Measured off both terminals rather than guessed at, twice having guessed
   * wrong: a token header there is a single row of about sixty pixels —
   * identity, then the figure somebody came for, then a run of
   * label-over-value pairs at a sixteen-pixel gap. gmgn's cell is a 12px label
   * over a 14px value with no border, no fill and no rule between it and the
   * next one; the gap alone does the grouping.
   *
   * Boxing each figure, which is what was here, turns the same numbers into a
   * table — and charges the chart a whole row of height for it.
   */
  .tokenhead {
    grid-area: head;
    min-height: 60px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 16px;
    padding: 0 12px;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 9px;
  }

  .tokenhead .id {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
    flex-shrink: 0;
  }

  .markbtn {
    flex-shrink: 0;
    line-height: 0;
    border-radius: 11px;
  }

  .markbtn:hover {
    opacity: 0.82;
  }

  .tokenhead .names {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .tokenhead .line {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .tickerbtn {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.4px;
    color: var(--t1);
  }

  .tickerbtn:hover {
    color: #8fa0ff;
  }

  .tokenhead .full {
    font-size: 11.5px;
    color: var(--t3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }

  .tokenhead .age {
    font-size: 10px;
    color: var(--t3);
  }

  /* A mark, not a word: square, no label, the glyph centred. */
  .chip.ico {
    width: 17px;
    padding: 0;
    justify-content: center;
  }

  .chip.said {
    border-color: rgba(70, 184, 125, 0.4);
    background: rgba(70, 184, 125, 0.14);
    color: var(--up);
  }

  .chip.warn {
    border-color: rgba(225, 92, 92, 0.34);
    background: rgba(225, 92, 92, 0.12);
    color: var(--down);
  }

  .chip {
    height: 17px;
    padding: 0 5px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 5px;
    border: 1px solid var(--line);
    background: var(--head);
    color: var(--t3);
    font-size: 10.5px;
    font-weight: 600;
    white-space: nowrap;
  }

  .chip:hover {
    border-color: var(--chip-hi);
    color: var(--t1);
  }

  .chip.crown {
    border-color: rgba(224, 163, 60, 0.3);
    background: rgba(224, 163, 60, 0.1);
    color: var(--warn);
  }

  /* ---------- the figure run ---------- */

  /*
   * Twelve figures, spread across whatever room the rail leaves. Space-between
   * rather than a fixed gap: at 1440 they breathe, and as the window narrows
   * they close up evenly instead of the last two falling off a cliff.
   */
  /*
   * Packed at a fixed gap, not spread.
   *
   * Two ways of spreading a row both looked wrong. space-between hands every
   * pixel of slack to the gaps, so a migrated pair — one figure fewer, having
   * no bonding curve — blew the row apart. Packing left and pinning the audit
   * put all that slack in one hole instead, half a screen of it.
   *
   * The artboard reads as even columns, so that is what these are: the cells
   * take the slack themselves and the gap stays at the drawn fifteen.
   */
  .figs {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 8px 15px;
  }

  /*
   * Below the width where twelve figures fit, they run sideways behind a
   * chevron instead of wrapping to a second row. Measured on gmgn, and it is
   * the right call: wrapping doubles the band's height and shoves the chart
   * down, while scrolling costs nothing and keeps the identity still.
   */
  .figscroll {
    display: none;
  }

  @container pulse (max-width: 1480px) {
    .figs {
      flex-wrap: nowrap;
      justify-content: flex-start;
      overflow-x: auto;
      scroll-behavior: smooth;
      scrollbar-width: none;
      -webkit-mask-image: linear-gradient(
        90deg,
        #000 calc(100% - 26px),
        transparent
      );
      mask-image: linear-gradient(90deg, #000 calc(100% - 26px), transparent);
    }

    .figs::-webkit-scrollbar {
      display: none;
    }

    .fig {
      flex-shrink: 0;
    }

    .figscroll {
      flex-shrink: 0;
      width: 22px;
      height: 32px;
      margin-left: -20px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--t2);
    }

    .figscroll:hover {
      color: var(--t1);
    }
  }

  .fig {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    text-align: left;
  }

  .fig > i {
    font-style: normal;
    font-size: 9.5px;
    font-weight: 500;
    color: var(--t3);
    white-space: nowrap;
  }

  .fig > b {
    display: flex;
    align-items: baseline;
    gap: 4px;
    font-size: 12.5px;
    font-weight: 500;
    letter-spacing: -0.2px;
    color: var(--t1);
    white-space: nowrap;
  }

  .fig > b em {
    font-style: normal;
    font-size: 9.5px;
    font-weight: 500;
    color: var(--t3);
  }

  .fig.asbtn:hover > i {
    color: var(--t3);
  }

  .fig.asbtn:hover > b {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* The figure somebody came for, leading the run at twice the size. */
  .capfig {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-shrink: 0;
  }

  .capfig b {
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.8px;
    line-height: 1;
    color: var(--t1);
  }

  .capfig em {
    font-style: normal;
    font-size: 11.5px;
    font-weight: 600;
  }

  .capfig:hover b {
    color: #ffffff;
  }

  /* ---------- the audit checks ---------- */

  .checks {
    flex-basis: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
    gap: 1px;
    background: var(--line);
    border: 1px solid var(--line);
    border-radius: 7px;
    overflow: hidden;
  }

  /*
   * Label over value, like every other figure on this page. Side by side is
   * what broke it: "Update authority" answers with a full address, and at a
   * seventh of the band's width the label wrapped into the value.
   */
  .ck {
    background: var(--head);
    padding: 7px 10px 8px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .ck i {
    font-style: normal;
    font-size: 9.5px;
    font-weight: 500;
    color: var(--t3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ck b {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ---------- the two columns ---------- */

  .main {
    grid-area: main;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /*
   * The rail is a column, not a fixed stack: seven blocks is more than a
   * window holds, and both terminals let theirs run long and scroll. The
   * scrollbar is hidden because the board hides every other one.
   */
  .side {
    grid-area: side;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    scrollbar-width: none;
  }

  .side::-webkit-scrollbar {
    display: none;
  }

  .side > .pn {
    flex-shrink: 0;
  }

  @container pulse (max-width: 1100px) {
    .main,
    .side {
      min-height: 0;
    }
  }

  /*
   * The chart and the narrow tape share a row, the way both terminals draw
   * them. The tape is the thing that folds, so the chart is the one that grows.
   */
  /*
   * The chart row takes what is left after the table has its share, and both
   * give before the page does. Measured on a 776px window: a 320px floor here
   * and a 320px ceiling on the table overflowed the footer by 72px and ran off
   * the screen, because neither was allowed to shrink.
   */
  .chartrow {
    flex: 1.4 1 0;
    min-height: 200px;
    min-width: 0;
    display: flex;
    gap: 8px;
  }

  /*
   * A control of ours, sitting in the chart toolbar's empty span.
   *
   * The toolbar is the chart package's own markup, so this is placed rather
   * than inserted: left of the camera it keeps clear of, on the same row, at
   * the height the row's other controls use.
   */
  .charttool {
    position: absolute;
    top: 7px;
    right: 46px;
    z-index: 2;
    height: 22px;
    padding: 0 8px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 6px;
    border: 1px solid var(--line);
    background: var(--panel);
    color: var(--t2);
    font-size: 10.5px;
    font-weight: 500;
  }

  .charttool:hover {
    border-color: var(--chip-hi);
    color: var(--t1);
  }

  .chartbox {
    flex-grow: 1;
    min-width: 0;
    min-height: 0;
    position: relative;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 9px;
    overflow: hidden;
  }

  /*
   * The chart's own theme, restated.
   *
   * Its canvas paints itself, but the toolbars, tooltips and plugin buttons
   * around it read the app's text and background tokens — sixty times over.
   * This screen is mounted outside the app's theme and carries its own
   * palette, so those tokens resolve to the light appearance and the chrome
   * comes out near-white on near-black. Every token the widget actually reads
   * is redeclared here, mapped to this palette. The values on the right are
   * what the app's dark theme gives them, measured rather than guessed.
   */
  .chartbox {
    --appearance-1: var(--bg);
    --text-3: var(--chip-hi);
    --text-9: var(--t4);
    --text-10: var(--t3);
    --text-11: var(--t2);
    --text-12: var(--t1);
    --background-1: var(--panel);
    --background-2: var(--chip);
    --background-3: var(--chip-hi);
    --background-4: #2a2b34;
    --background-5: #31323c;
    --background-7: #484a56;
    --background-9: var(--t4);
    --background-11: var(--t2);
    --backgroundA-1: rgba(0, 0, 0, 0);
    --backgroundA-2: rgba(255, 255, 255, 0.035);
    --backgroundA-3: rgba(255, 255, 255, 0.07);
    color: var(--t2);
  }

  /* ---------- panels in the side column ---------- */

  .pn {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 9px;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .pn > header {
    flex-shrink: 0;
    height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 11px;
    border-bottom: 1px solid var(--line);
    background: var(--head);
  }

  .pn > header h2 {
    margin: 0;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--t2);
  }

  .pn > header .right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .pn .in {
    padding: 11px;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .tag {
    height: 17px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
    background: var(--chip);
    color: var(--t4);
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }

  .hint {
    margin: 0;
    font-size: 11px;
    line-height: 16px;
    color: var(--t4);
  }

  /* ---------- trade ---------- */

  .sides {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }

  .sd {
    height: 34px;
    border-radius: 6px;
    border: 1px solid var(--line);
    background: var(--head);
    color: var(--t3);
    font-size: 13px;
    font-weight: 600;
  }

  .sd.buy.on {
    background: rgba(70, 184, 125, 0.16);
    border-color: rgba(70, 184, 125, 0.45);
    color: var(--up);
  }

  .sd.sell.on {
    background: rgba(225, 92, 92, 0.16);
    border-color: rgba(225, 92, 92, 0.45);
    color: var(--down);
  }

  .amount {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
    padding: 0 11px;
    background: #0d0e12;
    border: 1px solid var(--line);
    border-radius: 6px;
  }

  .amount:focus-within {
    border-color: var(--accent);
  }

  .amount .lb {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--t4);
  }

  .amount input {
    flex-grow: 1;
    min-width: 0;
    border: 0;
    background: none;
    outline: none;
    color: var(--t1);
    font-size: 17px;
    font-weight: 600;
    text-align: right;
  }

  .amount .unit {
    font-size: 11px;
    font-weight: 600;
    color: var(--t3);
  }

  .chips {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 5px;
  }

  .chip {
    height: 26px;
    border-radius: 5px;
    border: 1px solid var(--chip-hi);
    background: var(--chip);
    color: var(--t2);
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10.5px;
    font-weight: 600;
  }

  .chip:hover {
    background: var(--chip-hi);
    color: var(--t1);
  }

  .avail {
    margin: 0;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10.5px;
    color: var(--t4);
  }

  .go {
    height: 44px;
    border-radius: 7px;
    border: 0;
    font-size: 14px;
    font-weight: 600;
    color: #07080b;
  }

  .go.buy {
    background: var(--up);
  }

  .go.sell {
    background: var(--down);
    color: var(--t1);
  }

  .go:disabled {
    opacity: 0.35;
    cursor: default;
  }

  .gas {
    margin: 0;
    font-size: 10px;
    color: var(--t5);
    text-align: center;
  }

  /* ---------- flow ---------- */

  /* ---------- how it is moving ---------- */

  /*
   * The windows as tabs that already carry their own number — the shape of the
   * move has to be readable before anything is clicked.
   */
  .flowpn {
    overflow: hidden;
  }

  .flowpn .spans {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border-bottom: 1px solid var(--line);
  }

  .sp {
    padding: 8px 4px 7px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    border-bottom: 2px solid transparent;
  }

  .sp > i {
    font-style: normal;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4px;
    color: var(--t3);
  }

  .sp > b {
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: -0.2px;
  }

  .sp:hover {
    background: var(--head);
  }

  .sp.on {
    background: var(--head);
    border-bottom-color: var(--accent);
  }

  .sp.on > i {
    color: var(--t2);
  }

  .quad {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 4px 10px;
  }

  .quad.three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding-top: 8px;
    border-top: 1px solid var(--hair);
  }

  .qc {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .qc > i {
    font-style: normal;
    font-size: 10px;
    font-weight: 500;
    color: var(--t3);
  }

  .qc > b {
    display: flex;
    flex-direction: column;
    gap: 1px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.2px;
    color: var(--t1);
    white-space: nowrap;
  }

  .qc > b em {
    font-style: normal;
    font-size: 10px;
    font-weight: 500;
    color: var(--t3);
  }

  .split {
    height: 3px;
    border-radius: 2px;
    background: var(--down);
    overflow: hidden;
  }

  .split i {
    display: block;
    height: 100%;
    background: var(--up);
  }

  /* ---------- who holds it ---------- */

  /*
   * Naked, like the header run and for the same reason: six shares boxed in
   * hairlines read as a table of trivia, and these are the figures somebody
   * checks last before buying.
   */
  .risk {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
  }

  .rc {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .rc i {
    font-style: normal;
    font-size: 10px;
    font-weight: 500;
    color: var(--t3);
  }

  .rc b {
    font-size: 13.5px;
    font-weight: 500;
    letter-spacing: -0.2px;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .row .lb {
    font-size: 11px;
    color: var(--t4);
  }

  .row .addr {
    margin-left: auto;
    font-size: 11px;
    color: var(--t3);
  }

  .row .addr:hover {
    color: var(--t1);
  }

  /* ---------- the tape beside the chart ---------- */

  .sidetape {
    width: 254px;
    flex-shrink: 0;
    min-height: 0;
  }

  .sidetape > header {
    gap: 2px;
    padding: 0 6px 0 8px;
  }

  .sidetape > header .right {
    gap: 2px;
  }

  .tag.live {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--up);
  }

  .tag.live::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--up);
  }

  /* A followed wallet keeps its tint in the wide table too. */
  .tb2 tr.lit td {
    background: rgba(82, 111, 255, 0.07);
  }

  .trackedbadge {
    height: 15px;
    padding: 0 4px;
    margin-right: 6px;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    vertical-align: middle;
    border-radius: 4px;
    background: rgba(82, 111, 255, 0.16);
    color: #93a4ff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  .tape .seg button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .tape .seg button svg {
    color: #93a4ff;
  }

  /* The star rides with the address rather than in a column of its own. */
  .st .who {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    min-width: 0;
  }

  .ln {
    height: 35px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    color: var(--t3);
    border-bottom: 2px solid transparent;
  }

  .ln:hover {
    color: var(--t2);
  }

  .ln.on {
    color: var(--t1);
    font-weight: 600;
    border-bottom-color: var(--accent);
  }

  .ln em {
    font-style: normal;
    font-size: 9.5px;
    color: var(--t3);
  }

  .ib {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--t3);
  }

  .ib:hover {
    background: var(--chip);
    color: var(--t1);
  }

  .sthead,
  .st {
    display: grid;
    grid-template-columns: 62px 56px minmax(0, 1fr) 28px;
    align-items: center;
    gap: 6px;
    padding: 0 9px;
  }

  .sthead {
    height: 22px;
    flex-shrink: 0;
    border-bottom: 1px solid var(--line);
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.3px;
    color: var(--t3);
  }

  .sthead span:nth-child(2),
  .sthead span:nth-child(3),
  .sthead span:nth-child(4) {
    text-align: right;
  }

  .stbody {
    flex-grow: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
  }

  .stbody::-webkit-scrollbar {
    display: none;
  }

  .st {
    height: 24px;
    border-bottom: 1px solid var(--hair);
    font-size: 10.5px;
  }

  .st > span:nth-child(2),
  .st > .age {
    text-align: right;
    color: var(--t2);
  }

  .st .age {
    font-size: 10px;
    color: var(--t3);
  }

  .st .maker {
    text-align: right;
    color: var(--t2);
    text-decoration: none;
  }

  .st .maker:hover {
    color: #8fa0ff;
  }

  /* A followed wallet is the reason this lane exists; it is lit in every lane. */
  .st.lit {
    background: rgba(82, 111, 255, 0.07);
  }

  /*
   * Folded, the tape leaves a hand's width of itself on the seam: the chevron
   * back, its name, and a dot that keeps saying it is still running.
   */
  .tapestub {
    width: 16px;
    flex-shrink: 0;
    border-radius: 8px;
    background: var(--panel);
    border: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: var(--t3);
  }

  .tapestub:hover {
    border-color: var(--chip-hi);
    color: var(--t1);
  }

  .tapestub .vert {
    writing-mode: vertical-rl;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.6px;
  }

  .tapestub .live {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
  }

  /* ---------- wallets you track ---------- */

  .tracked {
    border-color: rgba(82, 111, 255, 0.3);
  }

  .tracked > header {
    background: rgba(82, 111, 255, 0.09);
    border-bottom-color: rgba(82, 111, 255, 0.22);
    color: #93a4ff;
  }

  .tracked > header h2 {
    color: #c8d0ff;
  }

  .count {
    font-size: 10.5px;
    font-weight: 600;
    color: #93a4ff;
  }

  .netline {
    flex-shrink: 0;
    padding: 9px 11px;
    border-bottom: 1px solid var(--line);
    display: flex;
    align-items: baseline;
    gap: 9px;
  }

  .netline b {
    font-size: 19px;
    font-weight: 600;
    letter-spacing: -0.6px;
  }

  .netline span {
    font-size: 10.5px;
    line-height: 1.35;
    color: var(--t3);
  }

  .netline em {
    font-style: normal;
    font-weight: 600;
  }

  .krows {
    padding: 4px 0 6px;
  }

  .kr {
    height: 24px;
    padding: 0 11px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto 56px 30px;
    align-items: center;
    gap: 8px;
    font-size: 11px;
  }

  /* What they are still in for, or a dash when the read does not reach them. */
  .kr .hold {
    text-align: right;
    font-size: 10.5px;
    color: var(--t2);
  }

  .kr a {
    color: var(--t1);
    text-decoration: none;
  }

  .kr a:hover {
    color: #8fa0ff;
  }

  .kr .mv {
    font-size: 10.5px;
    font-weight: 600;
  }

  .kr .ag {
    text-align: right;
    font-size: 10px;
    color: var(--t3);
  }

  /* ---------- the checks, the clone and the dev ---------- */

  .checklist {
    padding: 4px 0 6px;
  }

  .cl {
    height: 25px;
    padding: 0 11px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: var(--t2);
  }

  .cl b {
    margin-left: auto;
    font-size: 11px;
    font-weight: 600;
  }

  .cl .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .devstats {
    padding: 9px 11px;
    border-bottom: 1px solid var(--line);
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .devrows {
    padding: 4px 0 6px;
  }

  .dr {
    height: 34px;
    padding: 0 11px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
  }

  .dr:hover {
    background: var(--head);
  }

  .dr .dn {
    font-size: 11.5px;
    font-weight: 500;
    color: var(--t1);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dr .dv {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    flex-shrink: 0;
  }

  .dr .dv b {
    font-size: 11px;
    font-weight: 500;
    color: var(--t2);
  }

  .dr .dv em {
    font-style: normal;
    font-size: 9.5px;
    color: var(--t3);
  }

  /* ---------- the tabs under the chart ---------- */

  .tape > header {
    gap: 2px;
    padding: 0 10px 0 8px;
  }

  .mkscroll {
    flex-grow: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
  }

  .mkscroll::-webkit-scrollbar {
    display: none;
  }

  .tb2.mk .rk {
    color: var(--t3);
    font-size: 10.5px;
  }

  .tb2.mk .bs {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  .tb2.mk .bs em {
    font-style: normal;
    font-weight: 600;
  }

  .tb2.mk .bs i {
    font-style: normal;
    color: var(--t5);
  }

  .tb2 .who {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  /* The wallet that launched it, named rather than left to be looked up. */
  .devmark {
    height: 15px;
    padding: 0 4px;
    display: inline-flex;
    align-items: center;
    border-radius: 4px;
    background: rgba(224, 163, 60, 0.16);
    color: var(--warn);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  /* ---------- your own position ---------- */

  .mypos {
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .myhead {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }

  .myhead b {
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.8px;
    line-height: 1;
  }

  .myhead .pct {
    font-size: 13px;
    font-weight: 600;
  }

  .myhead .state {
    height: 18px;
    padding: 0 7px;
    display: inline-flex;
    align-items: center;
    border-radius: 5px;
    border: 1px solid var(--line);
    background: var(--head);
    color: var(--t3);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .myhead .state.open {
    border-color: rgba(70, 184, 125, 0.34);
    background: rgba(70, 184, 125, 0.12);
    color: var(--up);
  }

  .mygrid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
    gap: 14px;
  }

  .myc {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .myc > i {
    font-style: normal;
    font-size: 10px;
    font-weight: 500;
    color: var(--t3);
  }

  .myc > b {
    font-size: 15px;
    font-weight: 500;
    letter-spacing: -0.3px;
    color: var(--t1);
  }

  .myc > em {
    font-style: normal;
    font-size: 10.5px;
    color: var(--t3);
  }

  /* ---------- the tape ---------- */

  .tape {
    flex: 1 1 0;
    min-height: 150px;
    max-height: 420px;
  }

  .tapescroll {
    flex-grow: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
    position: relative;
  }

  .tapescroll::-webkit-scrollbar {
    display: none;
  }

  .tb2 {
    width: 100%;
    border-collapse: collapse;
  }

  .tb2 thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--head);
    height: 26px;
    padding: 0 11px;
    text-align: right;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--t4);
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }

  .tb2 thead th:first-child {
    text-align: left;
  }

  .tb2 tbody td {
    height: 27px;
    padding: 0 11px;
    text-align: right;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
    color: var(--t2);
    border-bottom: 1px solid var(--hair);
    white-space: nowrap;
  }

  .tb2 tbody td:first-child {
    text-align: left;
  }

  .tb2 tbody tr:hover td {
    background: rgba(255, 255, 255, 0.017);
  }

  .good {
    color: var(--up) !important;
  }

  .bad {
    color: var(--down) !important;
  }

  .soon {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    text-align: center;
    padding: 0 32px;
    font-size: 11.5px;
    line-height: 18px;
    color: var(--t4);
  }

  /*
   * The tape's own stops, last in the file on purpose: a container query and a
   * plain rule of the same specificity are settled by order, and these were
   * losing to the .sidetape width declared further down. Measured on staging:
   * the tape stayed 262px at every width until they moved here.
   */
  @container pulse (max-width: 1340px) {
    .sidetape {
      width: 224px;
    }
  }

  @container pulse (max-width: 1180px) {
    /* The chart loses before the tape does below this, so the tape goes and
       the wide table underneath carries the same trades. */
    .sidetape,
    .tapestub {
      display: none;
    }
  }
`
